import { useCallback, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  ModalCard,
  Note,
  Panel,
} from '../../../shared/components'
import { sendMessage } from '../../../shared/api/messages'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from './copy'
import { ADMIN_COPY, describeMessageError } from './adminCopy'

/**
 * E8's "Send message" — the school's office writing to one family.
 *
 * 🔴 IT REPLACED `alert('Send Message triggered')`, ON A PAGE THAT NAMES A REAL
 * CHILD. The endpoint behind it has existed since the messaging slice shipped:
 * `POST /players/:playerId/messages` admits TEACHER and ADMIN, and
 * `messages.service.ts` writes an admin as the TEACHER side on purpose —
 * *"an ADMIN writes as the school because an admin IS the school's office; that
 * is the account a family's number is registered against."* So this is not a new
 * channel, it is the C4/D6 thread, entered from the child rather than the inbox.
 *
 * 🔴 ADMIN ONLY, AND THE CONTROL IS NOT DRAWN FOR ANYBODY ELSE. `assertAdult`
 * refuses PRINCIPAL and COORDINATOR — *"oversight is not membership of somebody
 * else's correspondence"* — so a principal pressing this would collect a 403 on
 * a screen they are otherwise entitled to read. D4's rule: no control at all,
 * rather than one that cannot work. The caller gates it; this component states
 * the reason if the server refuses anyway.
 *
 * ⚠️ IT DOES NOT LOAD THE THREAD, AND THAT IS A CORRECTNESS DECISION RATHER THAN
 * A SAVING. `GET /players/:id/messages` MARKS THE OTHER SIDE'S MESSAGES SEEN as
 * a side effect of reading — so an admin opening this box to type one sentence
 * would silently tell the parent their earlier messages had been read by the
 * teacher they were addressed to. The conversation is the teacher's (D6) and the
 * parent's (C4); this writes into it without opening it.
 *
 * ⚠️ NO OPTIMISTIC ECHO AND NO LOCAL FALLBACK ON FAILURE. `TeacherThread` keeps
 * a failed send on screen as though it had gone; here the writer is told plainly
 * that nothing was sent, because the next thing they do depends on it.
 */

/** The server's own cap (`messages.service.ts`), stated where the box is drawn. */
export const MESSAGE_MAX = 2000

export interface StudentMessageComposerProps {
  playerId: string
  childName: string
  /** The guardian on the roster, or null when nobody is linked. */
  guardianName: string | null
  lang: Lang
  auth?: StaffAuth
  onClose: () => void
}

export function StudentMessageComposer({
  playerId,
  childName,
  guardianName,
  lang,
  auth,
  onClose,
}: StudentMessageComposerProps) {
  const t = ADMIN_COPY[lang]
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState<Error | null>(null)
  const [sent, setSent] = useState(false)

  const body = draft.trim()
  const ready = body.length > 0 && body.length <= MESSAGE_MAX && !sending

  const submit = useCallback(() => {
    if (!ready) return
    setSending(true)
    setFailed(null)
    void sendMessage(playerId, body, auth)
      .then(() => {
        setSent(true)
        setDraft('')
      })
      .catch((err: Error) => {
        setFailed(err)
      })
      .finally(() => {
        setSending(false)
      })
  }, [ready, playerId, body, auth])

  return (
    <ModalCard label={t.messageDialog} onClose={onClose} width="max-w-xl">
      <Panel>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">{t.messageDialog}</h2>
            {/*
              🔴 WHO IT REACHES, ABOVE THE BOX. An admin who assumes this is an
              SMS writes something time-critical into a channel nobody is
              watching — and the family this is addressed to may not have opened
              the app this week.
            */}
            <p className="mt-1 font-dash text-dash-xs font-bold text-muted">
              {/* ⚠️ "To No parent linked" IS NOT A SENTENCE. The preposition
                  belongs to a name; without one, the state is the whole line. */}
              {guardianName === null ? t.noParent : t.messageTo(guardianName)} · {childName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.messageClose}
            className="-mt-1 -mr-1 shrink-0 rounded-lg p-1 text-lg font-bold text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            ✕
          </button>
        </div>

        {sent ? (
          /*
           * ⚠️ THE CONFIRMATION NAMES THE RECIPIENT, and says something different
           * when there is nobody linked: the row is written either way, and a
           * message that reaches no family is not "sent".
           */
          <div className="mt-4 space-y-4">
            <Note tone="success" icon={<CheckIcon size={16} />}>
              {guardianName === null ? t.messageSentNobody : t.messageSent(guardianName)}
            </Note>
            <DashButton variant="secondary" onClick={onClose}>
              {t.messageClose}
            </DashButton>
          </div>
        ) : (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
            className="mt-4 space-y-3"
          >
            <p className="max-w-[62ch] font-dash text-dash-xs text-muted">{t.messageHint}</p>

            {/*
              🔴 NO GUARDIAN ON THE ROSTER IS A WARNING, NOT A BLOCKED FORM. The
              write succeeds — the thread belongs to the child — but nobody can
              read it, which is E7's "no parent contact" finding restated at the
              moment it matters. The admin can still write it and then go and
              link the parent; being told afterwards is what would waste the
              sentence they just typed.
            */}
            {guardianName === null && (
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {t.noParentWhy}
              </Note>
            )}

            <label className="flex flex-col gap-1.5">
              <span className="font-dash text-dash-xs font-bold text-muted">{t.messageWrite}</span>
              <textarea
                autoFocus
                value={draft}
                rows={5}
                maxLength={MESSAGE_MAX}
                placeholder={t.messagePlaceholder}
                disabled={sending}
                onChange={(e) => {
                  setDraft(e.target.value)
                }}
                className="w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 font-dash text-dash-sm text-ink outline-none transition-colors focus:border-primary disabled:opacity-60"
              />
            </label>

            {/* ⚠️ BOTH NUMBERS, like every other count in this tier's copy. */}
            <p className="tabular font-dash text-dash-2xs text-muted">
              {t.messageCount(draft.length, MESSAGE_MAX)}
            </p>

            {failed !== null && (
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {describeMessageError(failed, lang)}
              </Note>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <DashButton type="submit" variant="primary" disabled={!ready}>
                {sending ? t.messageSending : t.messageSend}
              </DashButton>
              <DashButton variant="secondary" onClick={onClose} disabled={sending}>
                {t.messageCancel}
              </DashButton>
            </div>
          </form>
        )}
      </Panel>
    </ModalCard>
  )
}

export default StudentMessageComposer
